import { useDispatch, useSelector } from 'react-redux'
import { Sparkles, AlertCircle, Loader2 } from 'lucide-react'
import type { AppDispatch, RootState } from '@/redux'
import { dashboardCreationActions } from '@/redux/slice/dashboardCreationSlice'
import { Button } from '@/components/ui/button'
import { buildDashboardThunk } from '@/services/DashboardViewService'
import { proposePanelsThunk } from '@/services/DashboardProposedPanelsService'
import type { FlowStateName, ProposedPanelV1 } from '@/types/DashboardV1.types'
import type { DashboardCreationState } from '@/redux/slice/dashboardCreationSlice'

/**
 * PanelsProposedBlock — FSM gate: awaiting_panels (Q5)
 *
 * Mockup: designs/ai-dashboard-platform-v3/2026-04-23-14-panels-proposed-v3.html
 * Brand guide: §12 (Panel Proposal Check Card — Component 3)
 *
 * v3 layout (Change C):
 * 1. "Recommended" section — panels with defaultInFirstDashboard=true (checked)
 * 2. "Explore More" section — remaining panels (unchecked, bg-[#FAFAFA])
 * Both sections fully visible — no "Show more" button.
 *
 * Panel type badge colors:
 *   kpi        → bg-accent-background text-accent border-[#BFDBFE]
 *   bar_chart  → bg-[#FFF7ED] text-[#C2410C] border-[#FDBA74]
 *   pivot_table → bg-[#F0FDF4] text-[#15803D] border-[#BBF7D0]
 *   table      → bg-[#F5F3FF] text-[#6D28D9] border-[#C4B5FD]
 *   line_chart → bg-accent-background text-accent border-[#BFDBFE]
 */

// ─── Panel type badge ─────────────────────────────────────────────────────────

const PANEL_TYPE_BADGE: Record<string, { label: string; className: string }> = {
  kpi: {
    label: 'KPI',
    className: 'bg-accent-background text-accent border-[#BFDBFE]',
  },
  bar_chart: {
    label: 'Bar Chart',
    className: 'bg-[#FFF7ED] text-[#C2410C] border-[#FDBA74]',
  },
  pivot_table: {
    label: 'Pivot Table',
    className: 'bg-[#F0FDF4] text-[#15803D] border-[#BBF7D0]',
  },
  table: {
    label: 'Table',
    className: 'bg-[#F5F3FF] text-[#6D28D9] border-[#C4B5FD]',
  },
  line_chart: {
    label: 'Line Chart',
    className: 'bg-accent-background text-accent border-[#BFDBFE]',
  },
}

function PanelTypeBadge({ panelType }: { panelType: string }) {
  const config = PANEL_TYPE_BADGE[panelType] ?? PANEL_TYPE_BADGE.kpi
  return (
    <span
      className={`inline text-[10.5px] font-semibold px-1.5 py-0.5 rounded-[10px] border ml-1.5 align-middle ${config.className}`}
    >
      {config.label}
    </span>
  )
}

// ─── Check icon SVG ────────────────────────────────────────────────────────────

const CheckSVG = () => (
  <svg
    viewBox="0 0 9 6"
    width="9"
    height="6"
    fill="none"
    stroke="white"
    strokeWidth="1.5"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M1 3l2 2 5-4.5" />
  </svg>
)

// ─── Panel row ─────────────────────────────────────────────────────────────────

interface PanelRowProps {
  panel: ProposedPanelV1
  checked: boolean
  onToggle: () => void
  isExploreMore: boolean
}

function PanelRow({ panel, checked, onToggle, isExploreMore }: PanelRowProps) {
  return (
    <label
      // `relative` is load-bearing — DO NOT remove. The hidden checkbox below
      // uses Tailwind's `sr-only`, which applies `position: absolute`. Without a
      // positioned ancestor the input's containing block walks up to the
      // initial containing block (the document), so the absolutely-positioned
      // checkboxes contribute to `documentElement.scrollHeight` even though
      // they're 1px and visually clipped. With 8 panels, the document grew to
      // ~1611px against a 723px viewport — a phantom outer scrollbar appeared
      // and clicking a checkbox triggered scroll-into-view on the document,
      // shoving the chat input upward. Making the label `relative` anchors the
      // absolute input inside the label's bounds (which live inside
      // ChatMessageRegion's internal scroll), eliminating the document growth.
      // Bug #2 from 2026-05-14 bugbash; verified via DOM measurement.
      className={`relative flex items-start gap-3 px-3.5 py-3 cursor-pointer transition-colors duration-150 focus-within:ring-2 focus-within:ring-focusRing focus-within:ring-offset-[-2px]
        ${isExploreMore ? 'bg-[#FAFAFA] hover:bg-accent-background' : 'bg-card hover:bg-accent-background'}`}
    >
      {/* Hidden real checkbox for semantics */}
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        onChange={onToggle}
        aria-label={`${checked ? 'Uncheck' : 'Include'} ${panel.title} panel`}
      />

      {/* Custom visual checkbox */}
      <div
        className={`w-4 h-4 rounded border-[1.5px] flex-shrink-0 mt-0.5 flex items-center justify-center transition-all duration-150
          ${checked ? 'bg-accent border-accent' : 'bg-card border-border'}`}
        aria-hidden="true"
      >
        {checked && <CheckSVG />}
      </div>

      {/* Panel content */}
      <div className="flex-1 min-w-0 flex flex-col items-start gap-0.5">
        <p className={`text-sm font-semibold mb-0.5 ${isExploreMore && !checked ? 'text-secondary' : 'text-primary'}`}>
          {panel.title}
          <PanelTypeBadge panelType={panel.panelType} />
        </p>
        <p className="text-xs text-secondary leading-snug text-left">{panel.description}</p>
      </div>
    </label>
  )
}

// ─── Panel list container ──────────────────────────────────────────────────────

function PanelList({
  panels,
  confirmedIds,
  onToggle,
  isExploreMore,
  ariaLabel,
}: {
  panels: ProposedPanelV1[]
  confirmedIds: string[]
  onToggle: (panelId: string) => void
  isExploreMore: boolean
  ariaLabel: string
}) {
  return (
    <div
      className="border border-border rounded-[12px] overflow-hidden bg-card divide-y divide-border mb-4"
      role="group"
      aria-label={ariaLabel}
    >
      {panels.map(panel => (
        <PanelRow
          key={panel.panelId}
          panel={panel}
          checked={confirmedIds.includes(panel.panelId)}
          onToggle={() => onToggle(panel.panelId)}
          isExploreMore={isExploreMore}
        />
      ))}
    </div>
  )
}

// ─── Loading skeleton (while proposal endpoint is in flight) ─────────────────

/**
 * PanelsSkeleton — shown while `proposedPanelsStatus === 'loading'`.
 * Re-uses the same skeleton treatment as FiltersBlock.SkeletonRow (loading
 * spinner + labelled pulse bars). Shown at the PanelsProposedBlock level so
 * the user knows panels are coming even though Step 5 is already visible.
 */
function PanelsSkeleton() {
  return (
    <div
      data-testid="panels-skeleton"
      aria-busy="true"
      aria-label="Loading panel proposals"
      className="flex flex-col gap-3 w-full"
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="text-sm font-semibold text-primary">Building your panel list…</span>
        <Loader2
          size={14}
          className="text-muted-foreground animate-spin"
          aria-hidden="true"
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <div className="h-12 w-full rounded-lg bg-muted animate-pulse" />
        <div className="h-12 w-full rounded-lg bg-muted animate-pulse" />
        <div className="h-12 w-full rounded-lg bg-muted animate-pulse" />
        <div className="h-12 w-[88%] rounded-lg bg-muted animate-pulse" />
      </div>
    </div>
  )
}

// ─── Error well ───────────────────────────────────────────────────────────────

interface PanelsErrorWellProps {
  /** Whether the error was a 409 FSM conflict (back-to-Step-4 CTA) or generic (retry CTA). */
  isFsmConflict: boolean
  onRetry: () => void
  onBackToFilters: () => void
}

/**
 * PanelsErrorWell — shown when `proposedPanelsStatus === 'error'` or `'fsm_conflict'`.
 * On generic error: retry CTA re-fires the thunk.
 * On FSM conflict (409): back-to-Step-4 escape hatch dispatches setFlowState.
 */
function PanelsErrorWell({ isFsmConflict, onRetry, onBackToFilters }: PanelsErrorWellProps) {
  return (
    <div
      role="alert"
      aria-live="assertive"
      data-testid="panels-error-well"
      className="bg-[#FEF2F2] border border-[#FECACA] rounded-lg p-3.5 flex flex-col gap-2"
    >
      <div className="flex items-center gap-2.5">
        <AlertCircle size={18} className="text-destructive flex-shrink-0" aria-hidden="true" />
        <span className="text-sm font-semibold text-primary">
          {isFsmConflict ? 'Please re-confirm your filters' : 'Couldn\'t load panel proposals'}
        </span>
      </div>
      <p className="text-xs text-secondary leading-relaxed">
        {isFsmConflict
          ? 'It looks like there was a sequencing issue. Go back to Step 4 and confirm your filters again.'
          : 'We had trouble generating your panel list. This is usually a temporary issue — try again in a moment.'}
      </p>
      <div className="flex items-center gap-2.5 mt-1">
        {isFsmConflict ? (
          <Button
            type="button"
            size="sm"
            className="h-8"
            aria-label="Go back to Step 4 to re-confirm filters"
            onClick={onBackToFilters}
          >
            Back to Filters
          </Button>
        ) : (
          <>
            <Button
              type="button"
              size="sm"
              className="h-8"
              aria-label="Try loading panel proposals again"
              onClick={onRetry}
            >
              Try again
            </Button>
            <button
              type="button"
              className="inline-flex items-center justify-center h-8 px-3 rounded-md border-[1.5px] border-border bg-card text-xs font-medium text-secondary hover:border-accent hover:text-accent hover:bg-accent-background focus-visible:ring-2 focus-visible:ring-focusRing focus-visible:ring-offset-2"
              aria-label="Go back to Step 4 to re-confirm filters"
              onClick={onBackToFilters}
            >
              Back to Filters
            </button>
          </>
        )}
      </div>
    </div>
  )
}

// ─── Build error well ─────────────────────────────────────────────────────────

interface BuildErrorWellProps {
  buildStatus: DashboardCreationState['buildStatus']
  /** domainId from UNSUPPORTED_DOMAIN error — undefined for other error types. */
  domainId: string | null
  /**
   * BE-reported flow_state on 409 INVALID_FSM_TRANSITION. Null when buildStatus
   * !== 'fsm_conflict' or when the BE omitted actualState. Bug #4 (2026-05-14).
   */
  actualState: string | null
  onRetry: () => void
  onBackToFilters: () => void
  /** fsm_conflict + actualState === 'complete' → navigate to canvas. */
  onViewDashboard: () => void
  /**
   * fsm_conflict + actualState is a known prior-step state → resume at that
   * step. Bug #4 (2026-05-14). The handler receives the actualState as a
   * narrowed FlowStateName so the type guard lives at the call site.
   */
  onResumeAtActualState: (actualState: FlowStateName) => void
}

/** Whitelisted FE FlowStateName set — type guard against BE actualState strings. */
const KNOWN_PRIOR_STATES: ReadonlyArray<FlowStateName> = [
  'awaiting_source',
  'awaiting_credential',
  'awaiting_domain',
  'awaiting_filters',
]
const isKnownPriorState = (s: string | null): s is FlowStateName =>
  s !== null && (KNOWN_PRIOR_STATES as readonly string[]).includes(s)

/** Human-friendly step label for the resume CTA copy. */
const PRIOR_STATE_LABEL: Record<FlowStateName, string> = {
  awaiting_source: 'Step 1 — Data Source',
  awaiting_credential: 'Step 2 — Credential',
  awaiting_domain: 'Step 3 — Domain',
  awaiting_filters: 'Step 4 — Filters',
  awaiting_panels: 'Step 5 — Panels',
  complete: 'Dashboard',
}

/**
 * BuildErrorWell — shown when `buildStatus` is an error variant.
 *
 * Error flavours:
 *   unsupported_domain → domain-specific message + back-to-Step-3 escape hatch.
 *   fsm_conflict       → branches on `actualState` (Bug #4, 2026-05-14):
 *     - 'complete' → "Already built / View dashboard" (existing behaviour).
 *     - 'awaiting_source' | 'awaiting_credential' | 'awaiting_domain' |
 *       'awaiting_filters' → "An earlier step needs re-confirming" + CTA that
 *       resumes at the BE's actualState. The user no longer sees a misleading
 *       "Already built" claim for the much more common case where the FE got
 *       ahead of the BE's persisted state.
 *     - unknown / null / `edit:<state>` / 'syncing' → safe fallback to the
 *       original "Already built" copy (mockup-stable; previous behaviour).
 *   missing_credential / unknown_panel → generic + retry + back-to-Step-4.
 *   error              → generic 5xx + retry + back-to-Step-4.
 */
function BuildErrorWell({
  buildStatus,
  domainId,
  actualState,
  onRetry,
  onBackToFilters,
  onViewDashboard,
  onResumeAtActualState,
}: BuildErrorWellProps) {
  const isUnsupportedDomain = buildStatus === 'unsupported_domain'
  const isFsmConflict = buildStatus === 'fsm_conflict'
  // Bug #4: only the "prior-step" sub-variant of fsm_conflict shows a resume CTA.
  const isPriorStateConflict = isFsmConflict && isKnownPriorState(actualState)

  let title: string
  let body: string

  if (isUnsupportedDomain) {
    title = 'Domain not yet supported'
    body = domainId
      ? `Panels for "${domainId}" are not yet supported in v1. Go back to Step 3 and select a supported domain (e.g. Enrollment).`
      : 'One of your selected domains is not yet supported in v1. Go back to Step 3 and select a different domain.'
  } else if (isPriorStateConflict) {
    // isPriorStateConflict implies isFsmConflict. actualState is one of the
    // known prior-step states — narrow + show targeted copy.
    const stepLabel = PRIOR_STATE_LABEL[actualState as FlowStateName]
    title = "We can't build the dashboard yet"
    body = `An earlier step still needs to be confirmed on our end (${stepLabel}). Click below to pick up where the server left off.`
  } else if (isFsmConflict) {
    // Safe fallback — 'complete', 'syncing', unknown, edit:<state>, or null.
    title = 'Dashboard already built'
    body = 'This dashboard has already been built. You can view it now.'
  } else if (buildStatus === 'missing_credential') {
    title = 'Credential issue'
    body = 'We could not access your CampMinder credential to complete the build. Try again, or go back to Step 4 and re-confirm your credential.'
  } else if (buildStatus === 'unknown_panel') {
    title = 'Panel not recognised'
    body = 'One of the selected panels was not recognised by the server. Try again, or go back to Step 4 to start fresh.'
  } else {
    title = 'Build failed'
    body = 'Something went wrong on our end. This is usually temporary — try again in a moment.'
  }

  return (
    <div
      role="alert"
      aria-live="assertive"
      data-testid="build-error-well"
      className="bg-[#FEF2F2] border border-[#FECACA] rounded-lg p-3.5 flex flex-col gap-2"
    >
      <div className="flex items-center gap-2.5">
        <AlertCircle size={18} className="text-destructive flex-shrink-0" aria-hidden="true" />
        <span className="text-sm font-semibold text-primary">{title}</span>
      </div>
      <p className="text-xs text-secondary leading-relaxed">{body}</p>
      <div className="flex items-center gap-2.5 mt-1">
        {isPriorStateConflict ? (
          <Button
            type="button"
            size="sm"
            className="h-8"
            aria-label={`Resume at ${PRIOR_STATE_LABEL[actualState as FlowStateName]}`}
            onClick={() => onResumeAtActualState(actualState as FlowStateName)}
          >
            Resume at {PRIOR_STATE_LABEL[actualState as FlowStateName]}
          </Button>
        ) : isFsmConflict ? (
          <Button
            type="button"
            size="sm"
            className="h-8"
            aria-label="View the already-built dashboard"
            onClick={onViewDashboard}
          >
            View dashboard
          </Button>
        ) : (
          <>
            <Button
              type="button"
              size="sm"
              className="h-8"
              aria-label="Try building the dashboard again"
              onClick={onRetry}
            >
              Try again
            </Button>
            <button
              type="button"
              className="inline-flex items-center justify-center h-8 px-3 rounded-md border-[1.5px] border-border bg-card text-xs font-medium text-secondary hover:border-accent hover:text-accent hover:bg-accent-background focus-visible:ring-2 focus-visible:ring-focusRing focus-visible:ring-offset-2"
              aria-label={isUnsupportedDomain ? 'Go back to Step 3 to change domain' : 'Go back to Step 4 to re-confirm filters'}
              onClick={onBackToFilters}
            >
              {isUnsupportedDomain ? 'Back to Step 3' : 'Back to Filters'}
            </button>
          </>
        )}
      </div>
    </div>
  )
}

// ─── PanelsProposedBlock ───────────────────────────────────────────────────────

export const PanelsProposedBlock = () => {
  const dispatch: AppDispatch = useDispatch()
  const proposedPanels = useSelector((state: RootState) => state.dashboardCreation.proposedPanels)
  const proposedPanelsStatus = useSelector(
    (state: RootState) => state.dashboardCreation.proposedPanelsStatus
  )
  const confirmedPanelIds = useSelector(
    (state: RootState) => state.dashboardCreation.confirmedPanelIds
  )
  const buildStatus = useSelector((state: RootState) => state.dashboardCreation.buildStatus)
  const buildErrorDomainId = useSelector(
    (state: RootState) => state.dashboardCreation.buildErrorDomainId
  )
  // Bug #4 (2026-05-14): the BE-reported actualState on a 409 build error. Drives
  // step-specific copy + CTA in BuildErrorWell so the FE no longer claims the
  // dashboard is "already built" when the real cause is a prior-step state.
  const buildErrorActualState = useSelector(
    (state: RootState) => state.dashboardCreation.buildErrorActualState
  )
  const token = useSelector((state: RootState) => state.user.token)
  const dashboardId = useSelector((state: RootState) => state.dashboardCreation.dashboardId)

  const recommended = proposedPanels.filter(p => p.defaultInFirstDashboard)
  const exploreMore = proposedPanels.filter(p => !p.defaultInFirstDashboard)

  const handleToggle = (panelId: string) => {
    dispatch(dashboardCreationActions.toggleConfirmedPanel(panelId))
  }

  const handleBuild = () => {
    if (!token || !dashboardId) return
    void dispatch(buildDashboardThunk(token, dashboardId))
  }

  const handleRetryBuild = () => {
    // Reset build status and re-fire build.
    dispatch(dashboardCreationActions.setBuildStatus('idle'))
    dispatch(dashboardCreationActions.setBuildErrorDomainId(null))
    dispatch(dashboardCreationActions.setBuildErrorActualState(null))
    if (!token || !dashboardId) return
    void dispatch(buildDashboardThunk(token, dashboardId))
  }

  const handleRetryPropose = () => {
    if (!token || !dashboardId) return
    const idempotencyKey =
      globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2)
    void dispatch(proposePanelsThunk(token, dashboardId, idempotencyKey))
  }

  const handleBackToFilters = () => {
    dispatch(dashboardCreationActions.setFlowState('awaiting_filters'))
  }

  const handleBackToDomains = () => {
    dispatch(dashboardCreationActions.setFlowState('awaiting_domain'))
  }

  /**
   * Bug #4 (2026-05-14): resume at the BE-reported actualState when a 409
   * INVALID_FSM_TRANSITION lands and the BE is at a non-`complete` prior step.
   * Sends the user back to whichever step the BE thinks they're on so they can
   * re-confirm and progress normally. Clears the build-error state so the
   * error well doesn't linger.
   *
   * Accepts the BE actualState as a parameter rather than reading from Redux
   * to keep the handler pure-ish and avoid stale-closure footguns.
   */
  const handleResumeAtActualState = (actualState: FlowStateName) => {
    dispatch(dashboardCreationActions.setBuildStatus('idle'))
    dispatch(dashboardCreationActions.setBuildErrorDomainId(null))
    dispatch(dashboardCreationActions.setBuildErrorActualState(null))
    dispatch(dashboardCreationActions.setFlowState(actualState))
  }

  // FSM-conflict CTA: dashboard is already complete on the server. Flip our
  // local flowState to 'complete' so the route-level renderer (DashboardByIdRoute
  // or the parent shell) renders <DashboardCanvas /> for the existing build.
  // (PR #331 Qodo §8.)
  const handleViewDashboard = () => {
    dispatch(dashboardCreationActions.setBuildStatus('idle'))
    dispatch(dashboardCreationActions.setBuildErrorDomainId(null))
    dispatch(dashboardCreationActions.setBuildErrorActualState(null))
    dispatch(dashboardCreationActions.setFlowState('complete'))
  }

  const selectedCount = confirmedPanelIds.length
  const isBuildLoading = buildStatus === 'loading'

  // ── Loading state — skeleton while propose endpoint is in flight.
  if (proposedPanelsStatus === 'loading') {
    return (
      <div data-testid="panels-proposed-block" className="flex flex-col gap-3 w-full">
        <PanelsSkeleton />
      </div>
    )
  }

  // ── Error / FSM-conflict state for *propose* — error well with retry or back-to-filters.
  if (proposedPanelsStatus === 'error' || proposedPanelsStatus === 'fsm_conflict') {
    return (
      <div data-testid="panels-proposed-block" className="flex flex-col gap-3 w-full">
        <PanelsErrorWell
          isFsmConflict={proposedPanelsStatus === 'fsm_conflict'}
          onRetry={handleRetryPropose}
          onBackToFilters={handleBackToFilters}
        />
      </div>
    )
  }

  // ── Build error state — shown after a failed POST /build call.
  const buildErrorStatuses: DashboardCreationState['buildStatus'][] = [
    'unsupported_domain', 'missing_credential', 'unknown_panel', 'fsm_conflict', 'error',
  ]
  if (buildErrorStatuses.includes(buildStatus)) {
    return (
      <div data-testid="panels-proposed-block" className="flex flex-col gap-3 w-full">
        <BuildErrorWell
          buildStatus={buildStatus}
          domainId={buildErrorDomainId}
          actualState={buildErrorActualState}
          onRetry={handleRetryBuild}
          onBackToFilters={buildStatus === 'unsupported_domain' ? handleBackToDomains : handleBackToFilters}
          onViewDashboard={handleViewDashboard}
          onResumeAtActualState={handleResumeAtActualState}
        />
      </div>
    )
  }

  return (
    <div data-testid="panels-proposed-block" className="flex flex-col gap-3 w-full">
      {/* Recommended section */}
      {recommended.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-2" aria-label="Recommended panels section">
            <span className="text-[11px] font-bold uppercase tracking-wide text-accent">
              Recommended ({confirmedPanelIds.filter(id => recommended.some(p => p.panelId === id)).length} of {recommended.length} checked)
            </span>
            <span className="text-[11px] text-secondary">AI-seeded defaults</span>
          </div>
          <PanelList
            panels={recommended}
            confirmedIds={confirmedPanelIds}
            onToggle={handleToggle}
            isExploreMore={false}
            ariaLabel="Recommended panels — all checked by default"
          />
        </>
      )}

      {/* Explore More section */}
      {exploreMore.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-2 mt-2" aria-label="Explore more panels section">
            <span className="text-[11px] font-bold uppercase tracking-wide text-secondary">
              Explore More ({exploreMore.length} available)
            </span>
            <span className="text-[11px] text-secondary">Optional - unchecked by default</span>
          </div>
          <PanelList
            panels={exploreMore}
            confirmedIds={confirmedPanelIds}
            onToggle={handleToggle}
            isExploreMore
            ariaLabel="Explore more panels - unchecked by default, add if you want them"
          />
        </>
      )}

      {/* Defensive empty state — only shown when status is idle/ready but no panels.
          Should not be hit in the happy path (the endpoint always returns ≥1 panel
          for a non-trivial domain), but kept as a fallback per plan defensive note. */}
      {proposedPanels.length === 0 && (
        <p className="text-sm text-muted-foreground italic">No panels proposed yet.</p>
      )}

      {/* Selection summary */}
      {proposedPanels.length > 0 && (
        <p className="text-xs text-secondary mb-1">
          <strong className="text-primary">{selectedCount} panel{selectedCount !== 1 ? 's' : ''}</strong>{' '}
          selected · Uncheck any you don&apos;t want before building
        </p>
      )}

      {/* Build CTA */}
      <Button
        type="button"
        className="w-full font-semibold"
        disabled={selectedCount === 0 || isBuildLoading}
        aria-label={
          isBuildLoading
            ? 'Building dashboard…'
            : `Build my dashboard with ${selectedCount} selected panels`
        }
        onClick={handleBuild}
      >
        {isBuildLoading ? (
          <>
            <Loader2 size={15} className="mr-1.5 animate-spin" aria-hidden="true" />
            Building…
          </>
        ) : (
          <>
            <Sparkles size={15} className="mr-1.5" aria-hidden="true" />
            Build My Dashboard
          </>
        )}
      </Button>
    </div>
  )
}
